import { Money, type BillingPreference, type CreateCustomerInput, type Currency } from '@chukua/common';
import { Alert, Button, Drawer, Form, Input, Switch } from 'antd';
import { Building2 } from 'lucide-react';
import { BillingAddressFields } from './BillingAddressFields';
import { TermsAndCreditFields } from './TermsAndCreditFields';

interface CreateCustomerFormValues {
  name: string;
  kraPin: string;
  billingAddressLine1: string;
  billingAddressLine2?: string;
  billingAddressCity: string;
  billingAddressCountry: string;
  paymentTermsDays: number;
  creditLimitAmount: number;
  creditLimitCurrency: Currency;
  billingPreference: BillingPreference;
  poNumberRequired: boolean;
}

interface CreateCustomerDrawerProps {
  open: boolean;
  error: string | null;
  submitting: boolean;
  onCreate: (input: CreateCustomerInput) => Promise<boolean>;
  onClose: () => void;
}

function toCreateCustomerInput(values: CreateCustomerFormValues): CreateCustomerInput {
  return {
    name: values.name,
    kraPin: values.kraPin,
    billingAddress: {
      line1: values.billingAddressLine1,
      line2: values.billingAddressLine2 || undefined,
      city: values.billingAddressCity,
      country: values.billingAddressCountry,
    },
    paymentTermsDays: values.paymentTermsDays,
    creditLimit: Money.parse(String(values.creditLimitAmount), values.creditLimitCurrency).toJSON(),
    billingPreference: values.billingPreference,
    poNumberRequired: values.poNumberRequired,
  };
}

/** Name, KRA PIN, billing address, terms, credit limit and billing preference — contacts are added from the customer's own detail page after creation. */
export function CreateCustomerDrawer({ open, error, submitting, onCreate, onClose }: CreateCustomerDrawerProps) {
  const [form] = Form.useForm<CreateCustomerFormValues>();

  async function submit(values: CreateCustomerFormValues) {
    const created = await onCreate(toCreateCustomerInput(values));
    if (created) {
      form.resetFields();
      onClose();
    }
  }

  return (
    <Drawer open={open} onClose={onClose} title="Create customer" placement="right" size={480} destroyOnClose>
      {error && <Alert className="mb-5" type="error" title={error} showIcon />}
      <Form<CreateCustomerFormValues>
        form={form}
        layout="vertical"
        requiredMark={false}
        onFinish={submit}
        disabled={submitting}
        initialValues={{ paymentTermsDays: 30, creditLimitAmount: 0, creditLimitCurrency: 'KES', billingPreference: 'monthly', poNumberRequired: false }}
      >
        <Form.Item name="name" label="Name" rules={[{ required: true, message: 'Enter a name' }]}>
          <Input prefix={<Building2 className="size-4 text-muted" />} autoFocus />
        </Form.Item>
        <Form.Item
          name="kraPin"
          label="KRA PIN"
          rules={[{ required: true, pattern: /^[A-Za-z]\d{9}[A-Za-z]$/, message: 'One letter, nine digits, one letter' }]}
        >
          <Input className="uppercase" />
        </Form.Item>
        <BillingAddressFields />
        <TermsAndCreditFields />
        <Form.Item name="poNumberRequired" label="PO number required" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item className="mb-0 pt-2">
          <Button type="primary" htmlType="submit" loading={submitting}>
            Create customer
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
}
